import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2, Search, ShoppingBag, FileText, User, ArrowRight,
  CircleDashed, RefreshCw, Landmark,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from './ui/command';
import { DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';

/**
 * Global Cmd+K / Ctrl+K command palette.
 *
 * Carlos's exact requirement: "is the TikTok order on the platform, or how
 * do we confirm?". When the search returns 0 results we render an explicit
 * "❌ NOT in the platform yet" panel with a "Sync from TikTok" CTA so the
 * answer never leaves the user guessing.
 *
 * Mounted once globally inside AppShell. Listens for the Cmd/Ctrl+K key
 * combination and opens itself; closes on Esc.
 */
export default function CommandPalette() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const locale = lang === 'es' ? 'es' : 'en-US';

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [syncing, setSyncing] = useState(false);

  // ---- Hotkey: Cmd+K (Mac) / Ctrl+K (Win/Linux) ----------------------
  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpenEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('command-palette:open', onOpenEvt);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('command-palette:open', onOpenEvt);
    };
  }, []);

  // Debounce the query so we don't spam the backend on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 220);
    return () => clearTimeout(id);
  }, [query]);

  // Reset query when palette closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);

  const enabled = debounced.length >= 2;
  const searchQuery = useQuery({
    queryKey: ['global-search', debounced],
    enabled,
    queryFn: async () =>
      (await api.get('/search', { params: { q: debounced, limit: 8 } })).data,
    staleTime: 30_000,
  });

  const groups = searchQuery.data?.groups || {
    orders: [], payouts: [], settlements: [], customers: [],
  };
  const depositRefs = searchQuery.data?.deposit_references || [];
  const totalMatches = (searchQuery.data?.total_matches) ?? 0;
  const showEmpty = enabled && !searchQuery.isLoading && totalMatches === 0;

  // Heuristic: if the query looks like a TikTok order id, offer to pull
  // it directly from TikTok via the existing sync endpoint. NEVER offer it for
  // a term that matches an existing bank payout / statement id — that is a
  // payout, not an order.
  const looksLikeOrderId = useMemo(() => {
    if (searchQuery.data?.matched_payout_or_statement) return false;
    return /^[A-Za-z0-9_-]{8,}$/.test(debounced);
  }, [debounced, searchQuery.data]);

  const handleSelect = (path) => {
    setOpen(false);
    navigate(path);
  };

  const handleSyncFromTikTok = async () => {
    if (!window.confirm(t('search.sync_confirm_body'))) return;
    try {
      setSyncing(true);
      const { data } = await api.post('/tiktok/sync/orders', null, {
        params: { hours_back: 24, max_pages: 5, page_size: 50 },
      });
      const pulled = data?.pulled ?? 0;
      const processed = data?.processed ?? 0;
      toast.success(
        t('search.sync_done')
          .replace('{{pulled}}', pulled)
          .replace('{{processed}}', processed)
      );
      // Re-run the search after the sync so the row appears (if it was
      // really there).
      searchQuery.refetch();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || '';
      toast.error(`${t('search.sync_error')}${detail ? ` — ${detail}` : ''}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      data-testid="command-palette"
    >
      {/* Visually-hidden title + description for screen readers — also
          silences Radix DialogContent a11y warning. */}
      <DialogTitle className="sr-only">{t('search.open_palette')}</DialogTitle>
      <DialogDescription className="sr-only">{t('search.start_typing_hint')}</DialogDescription>
      <CommandInput
        placeholder={t('search.placeholder')}
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        {/* Empty / hint states */}
        {!enabled && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground"
                data-testid="command-palette-hint">
            <Search className="mx-auto mb-3 h-6 w-6 text-muted-foreground/40" strokeWidth={1.5} />
            <div>{t('search.start_typing')}</div>
            <div className="mt-2 text-[11px] text-muted-foreground/70">
              {t('search.start_typing_hint')}
            </div>
          </div>
        )}

        {enabled && searchQuery.isLoading && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground"
                data-testid="command-palette-loading">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        )}

        {showEmpty && (
          <div className="px-4 py-6 text-sm" data-testid="command-palette-empty">
            <div className="flex items-start gap-3 rounded-lg border p-3"
                  style={{
                    borderColor: 'rgba(239,68,68,0.35)',
                    background: 'rgba(239,68,68,0.07)',
                  }}>
              <CircleDashed className="mt-0.5 h-5 w-5 text-red-500 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <div className="font-semibold text-foreground">
                  {t('search.not_found_title').replace('{{q}}', debounced)}
                </div>
                <div className="text-[12px] text-muted-foreground mt-0.5">
                  {t('search.not_found_subtitle')}
                </div>
                {looksLikeOrderId && isAdmin && (
                  <Button
                    type="button"
                    size="sm"
                    className="mt-3"
                    onClick={handleSyncFromTikTok}
                    disabled={syncing}
                    data-testid="command-palette-sync-now"
                  >
                    {syncing
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <RefreshCw className="h-4 w-4 mr-2" />}
                    {t('search.sync_from_tiktok')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {enabled && !searchQuery.isLoading && groups.orders.length > 0 && (
          <CommandGroup heading={t('search.group_orders')}>
            {groups.orders.map((o) => (
              <CommandItem
                key={`o-${o.tiktok_order_id}`}
                value={`order-${o.tiktok_order_id}`}
                onSelect={() => handleSelect(`/orders?focus=${encodeURIComponent(o.tiktok_order_id)}`)}
                data-testid={`command-palette-order-${o.tiktok_order_id}`}
              >
                <ShoppingBag className="opacity-60" strokeWidth={1.5} />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="truncate font-mono text-[12px]">{o.tiktok_order_id}</span>
                  <span className="truncate text-[11px] text-muted-foreground">
                    {o.customer_name || '—'}
                    {o.customer_email ? ` · ${o.customer_email}` : ''}
                    {o.day_bucket ? ` · ${formatDate(o.day_bucket, locale)}` : ''}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground/70 ml-2 shrink-0">
                  {o.total_amount != null ? formatCurrency(o.total_amount, locale) : ''}
                </span>
                <ArrowRight className="opacity-30 ml-2 shrink-0" strokeWidth={1.5} />
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {enabled && !searchQuery.isLoading && (groups.payouts || []).length > 0 && (
          <>
            {groups.orders.length > 0 && <CommandSeparator />}
            <CommandGroup heading={t('search.group_payouts')}>
              {groups.payouts.map((p) => (
                <CommandItem
                  key={`p-${p.group_key}`}
                  value={`payout-${p.group_key}`}
                  onSelect={() => handleSelect(
                    `/depositos?payment=${encodeURIComponent(p.payment_id || p.group_key)}`)}
                  data-testid={`command-palette-payout-${p.payment_id || p.group_key}`}
                >
                  <Landmark className="opacity-60" strokeWidth={1.5} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-mono text-[12px]">
                      {p.payment_id || p.group_key}
                    </span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {p.statement_count} statements
                      {p.date ? ` · ${formatDate(p.date, locale)}` : ''}
                    </span>
                  </div>
                  <span className="text-[11px] text-muted-foreground/70 ml-2 shrink-0">
                    {p.total != null ? formatCurrency(p.total, locale) : ''}
                  </span>
                  <ArrowRight className="opacity-30 ml-2 shrink-0" strokeWidth={1.5} />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {enabled && !searchQuery.isLoading && depositRefs.length > 0 && (
          <>
            {(groups.orders.length > 0 || (groups.payouts || []).length > 0)
              && <CommandSeparator />}
            <CommandGroup heading={t('search.group_deposit_refs')}>
              {depositRefs.map((r, i) => {
                const label = r.match_kind === 'invoice'
                  ? t('deposits.find_by_invoice').replace('{{n}}', r.doc_number || r.invoice_id || '—')
                  : t('deposits.find_by_order').replace('{{n}}', (r.order_ids || [])[0] || '—');
                return (
                  <CommandItem
                    key={`dr-${r.payout_id}-${r.match_kind}-${i}`}
                    value={[
                      'deposit-ref', r.payout_id, r.match_kind, String(i),
                      r.doc_number || '', r.invoice_id || '',
                      ...(r.order_ids || []),
                      r.payment_id || '', r.payment_id_last7 || '',
                    ].filter(Boolean).join(' ')}
                    onSelect={() => handleSelect(`/depositos/${encodeURIComponent(r.payout_id)}`)}
                    data-testid={`command-palette-deposit-ref-${r.payout_id}-${r.match_kind}`}
                  >
                    <Landmark className="opacity-60" strokeWidth={1.5} />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate text-[12px]">
                        {t('search.deposit_ref_in_payout')
                          .replace('{{label}}', label)
                          .replace('{{payout}}', r.payout_id)}
                      </span>
                      <span className="truncate text-[11px] text-muted-foreground">
                        {t(`deposits.find_by_kind_${r.match_kind}`)}
                        {r.payment_id_last7 ? ` · ${r.payment_id_last7}` : ''}
                        {r.date ? ` · ${formatDate(r.date, locale)}` : ''}
                      </span>
                    </div>
                    <span className="text-[11px] text-muted-foreground/70 ml-2 shrink-0">
                      {r.amount != null ? formatCurrency(r.amount, locale) : ''}
                    </span>
                    <ArrowRight className="opacity-30 ml-2 shrink-0" strokeWidth={1.5} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {enabled && !searchQuery.isLoading && groups.settlements.length > 0 && (
          <>
            {(groups.orders.length > 0 || (groups.payouts || []).length > 0) && <CommandSeparator />}
            <CommandGroup heading={t('search.group_settlements')}>
              {groups.settlements.map((s) => (
                <CommandItem
                  key={`s-${s.settlement_id}`}
                  value={`settlement-${s.settlement_id}`}
                  onSelect={() => handleSelect(`/depositos/${encodeURIComponent(s.settlement_id)}`)}
                  data-testid={`command-palette-settlement-${s.settlement_id}`}
                >
                  <FileText className="opacity-60" strokeWidth={1.5} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-mono text-[12px]">{s.settlement_id}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {s.settlement_date ? formatDate(s.settlement_date, locale) : ''}
                      {s.status ? ` · ${s.status}` : ''}
                    </span>
                  </div>
                  <span className="text-[11px] text-muted-foreground/70 ml-2 shrink-0">
                    {s.expected_payout != null ? formatCurrency(s.expected_payout, locale) : ''}
                  </span>
                  <ArrowRight className="opacity-30 ml-2 shrink-0" strokeWidth={1.5} />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {enabled && !searchQuery.isLoading && groups.customers.length > 0 && (
          <>
            {(groups.orders.length > 0 || groups.settlements.length > 0) && <CommandSeparator />}
            <CommandGroup heading={t('search.group_customers')}>
              {groups.customers.map((c) => (
                <CommandItem
                  key={`c-${c.qbo_customer_id}`}
                  value={`customer-${c.qbo_customer_id}`}
                  onSelect={() => handleSelect(`/orders?focus=customer:${encodeURIComponent(c.qbo_customer_id)}`)}
                  data-testid={`command-palette-customer-${c.qbo_customer_id}`}
                >
                  <User className="opacity-60" strokeWidth={1.5} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate text-[12px] font-medium">{c.display_name || '—'}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {c.email || ''}{c.phone ? ` · ${c.phone}` : ''}
                    </span>
                  </div>
                  <ArrowRight className="opacity-30 ml-2 shrink-0" strokeWidth={1.5} />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* "More" hint: total match count */}
        {enabled && !searchQuery.isLoading && totalMatches > 0 && (
          <div className="px-3 py-2 text-[11px] text-muted-foreground/70 border-t"
                style={{ borderColor: 'var(--border-subtle)' }}>
            {t('search.results_count').replace('{{count}}', totalMatches)}
            <CommandShortcut>↵</CommandShortcut>
          </div>
        )}

        {/* Always render an Empty stub so cmdk doesn't complain */}
        <CommandEmpty className="hidden" />
      </CommandList>
    </CommandDialog>
  );
}
